import React, { useState, useEffect, useMemo } from 'react';
import Timeline from 'react-calendar-timeline';
import moment from 'moment';
import 'react-calendar-timeline/dist/Timeline.scss';
import { Icon } from '@iconify/react';
import { useFormBuilder } from '../utils/useFormBuilder';
import { validateForm as validateFormUtil } from '../utils/useValidator';
import { useTableFilters } from '../utils/useTableFilters';
import { getAll } from '../api';
import {
  getAssetAssignments,
  createAssetAssignments,
  updateAssetAssignments,
} from '../api/asset-assignmentsAPI';
import { getProjects } from '../api/projectsAPI';
import { getCustomers } from '../api/customersAPI';
import { getProjectStatus } from '../api/project-statusAPI';
import './AssetAssignmentLayer.css';

const AssetAssignmentLayer = () => {
  const filters = useMemo(() => [
    { field: 'groupId', type: 'dropDown', label: 'Asset Group', api: 'groups', displayField: 'groupName' },
    { field: 'classId', type: 'dropDown', label: 'Asset Class', api: 'class', displayField: 'className' },
    { field: 'departmentId', type: 'dropDown', label: 'Department', api: 'hierarchy-structures', displayField: 'name' },
    { field: 'plantStatusId', type: 'dropDown', label: 'Asset Status', api: 'plant-status', displayField: 'status' },
    {
      field: 'assetIds',
      type: 'dropDown',
      label: 'Asset',
      api: 'assets/for-dropdown',
      displayField: (a) => `${a.assetNumber || ''} ${a.assetDescription || ''}`,
    },
  ], []);

  const {
    appliedFilterParams,
    showDropdown,
    setShowDropdown,
    renderFilterDropdown,
    renderFilterBadges,
    resetFilters,
    applyFilters,
  } = useTableFilters(filters);

  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newAssignment, setNewAssignment] = useState({});
  const [errors, setErrors] = useState({});
  const [viewMode, setViewMode] = useState('month');
  const [currentDate, setCurrentDate] = useState(moment());

  const assignmentFields = useMemo(() => [
    {
      field: 'assetId',
      label: 'Asset',
      type: 'select',
      size: 12,
      api: 'assets/for-dropdown',
      displayField: (a) => `${a.assetNumber || ''} ${a.assetDescription || ''}`,
      required: true,
    },
    { field: 'projectId', label: 'Project', type: 'select', size: 12, api: 'projects', displayField: 'name', required: true },
    { field: 'startDate', label: 'Start', type: 'date', size: 6, required: true },
    { field: 'endDate', label: 'End', type: 'date', size: 6, required: true },
    { field: 'status', label: 'Status', type: 'select', size: 6, api: 'project-status', displayField: 'name', required: true },
  ], []);

  const { renderField } = useFormBuilder(assignmentFields, newAssignment, setNewAssignment, errors);

  const getRange = () => {
    if (viewMode === 'day') {
      return [moment(currentDate).startOf('day'), moment(currentDate).endOf('day')];
    }
    if (viewMode === 'week') {
      return [moment(currentDate).startOf('week'), moment(currentDate).endOf('week')];
    }
    if (viewMode === 'month') {
      return [moment(currentDate).startOf('month'), moment(currentDate).endOf('month')];
    }
    return [moment(currentDate).startOf('year'), moment(currentDate).endOf('year')];
  };

  const loadData = async () => {
      try {
        const [assignmentsResp, assets, projectsResp, customers, statuses] = await Promise.all([
          getAssetAssignments(),
          getAll('assets/for-dropdown'),
          getProjects(),
          getCustomers(),
          getProjectStatus(),
        ]);

        setAssignments(assignmentsResp);
        setStatusList(statuses);

        const assetMap = Object.fromEntries(assets.map(a => [a.id, a]));
        const projectList = Array.isArray(projectsResp?.projects)
          ? projectsResp.projects
          : Array.isArray(projectsResp)
            ? projectsResp
            : [];
        const projectMap = Object.fromEntries(projectList.map(p => [p.id, p]));
        const customerMap = Object.fromEntries(customers.map(c => [c.id, c]));
        const statusMap = Object.fromEntries(statuses.map(s => [s.id, s]));

        let filtered = assignmentsResp;
        if (appliedFilterParams.groupId?.length) {
          filtered = filtered.filter(a => appliedFilterParams.groupId.includes(assetMap[a.assetId]?.groupId));
        }
        if (appliedFilterParams.classId?.length) {
          filtered = filtered.filter(a => appliedFilterParams.classId.includes(assetMap[a.assetId]?.classId));
        }
        if (appliedFilterParams.departmentId?.length) {
          filtered = filtered.filter(a => appliedFilterParams.departmentId.includes(assetMap[a.assetId]?.assetDepartmentId));
        }
        if (appliedFilterParams.plantStatusId?.length) {
          filtered = filtered.filter(a => appliedFilterParams.plantStatusId.includes(assetMap[a.assetId]?.plantStatusId));
        }
        if (appliedFilterParams.assetIds?.length) {
          filtered = filtered.filter(a => appliedFilterParams.assetIds.includes(a.assetId));
        }

        const groupMap = {};
        const newItems = [];

        filtered.forEach(a => {
          const asset = assetMap[a.assetId] || {};
          const project = projectMap[a.projectId] || {};
          const customer = customerMap[project.customerId] || {};
          const status = statusMap[a.status] || {};
          const color = status.colours?.bootstrapName || 'secondary';
          const isConfirmed = status.name?.toLowerCase() === 'confirmed';

          const groupId = String(a.assetId);
          if (!groupMap[groupId]) {
            groupMap[groupId] = {
              id: groupId,
              title: `${asset.assetNumber || ''} ${asset.assetDescription || ''}`,
            };
          }

          newItems.push({
            id: a.id,
            group: groupId,
            title: `${customer.name || ''} ${project.name || ''}`,
            start_time: moment(a.startDate || a.start_date),
            end_time: moment(a.endDate || a.end_date),
            className: isConfirmed ? 'bg-success-striped' : `bg-${color}`,
          });
        });

        setGroups(Object.values(groupMap));
        setItems(newItems);
        setAssignments(filtered);
      } catch (err) {
        console.error('Error loading asset assignments:', err);
      }
    };

  useEffect(() => {
    loadData();
  }, [appliedFilterParams]);

  const handleAdd = () => {
    setNewAssignment({
      startDate: moment().format('YYYY-MM-DD'),
      endDate: moment().add(7, 'days').format('YYYY-MM-DD'),
      status: 1,
    });
    setErrors({});
    setEditingId(null);
    setShowModal(true);
  };

  const handleEdit = (id) => {
    const data = assignments.find((a) => a.id === id);
    if (!data) return;
    setNewAssignment({
      assetId: data.assetId,
      projectId: data.projectId,
      startDate: data.startDate || data.start_date,
      endDate: data.endDate || data.end_date,
      status: data.status ?? 1,
    });
    setErrors({});
    setEditingId(id);
    setShowModal(true);
  };

  const validateAssignment = () => {
    const errs = validateFormUtil(assignmentFields, newAssignment);
    const start = moment(newAssignment.startDate);
    const end = moment(newAssignment.endDate);
    const status = statusList.find(s => s.id === newAssignment.status)?.name?.toLowerCase() || '';

    if (start.isValid() && end.isValid() && !end.isAfter(start)) {
      errs.endDate = 'End date must be after start date';
    }

    if (['tentative', 'confirmed'].includes(status) && !start.isAfter(moment())) {
      errs.startDate = 'Start date must be in the future';
    }

    if (status === 'completed' && !end.isBefore(moment())) {
      errs.endDate = 'End date must be in the past';
    }

    if (status === 'active' && !start.isBefore(moment())) {
      errs.startDate = 'Start date must be in the past';
    }

    if (start.isBefore(moment(), 'day')) {
      const overlap = assignments.some(a => {
        if (editingId && a.id === editingId) return false;
        if (a.assetId !== newAssignment.assetId) return false;
        const aStart = moment(a.startDate || a.start_date);
        const aEnd = moment(a.endDate || a.end_date);
        return start.isSameOrBefore(aEnd, 'day') && end.isSameOrAfter(aStart, 'day');
      });
      if (overlap) {
        errs.startDate = 'Overlaps with existing assignment';
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validateAssignment()) return;
    try {
      if (editingId) {
        await updateAssetAssignments(editingId, newAssignment);
      } else {
        await createAssetAssignments(newAssignment);
      }
      setShowModal(false);
      setNewAssignment({});
      await loadData();
    } catch (err) {
      console.error('Error saving assignment', err);
    }
  };

  const [toastMsg, setToastMsg] = useState('');

  useEffect(() => {
    if (toastMsg) {
      const t = setTimeout(() => setToastMsg(''), 5000);
      return () => clearTimeout(t);
    }
  }, [toastMsg]);

  const validateDates = (assignmentId, assetId, start, end, statusId) => {
    const statusName = statusList.find(s => s.id === statusId)?.name?.toLowerCase() || '';
    if (end.isSameOrBefore(start)) return 'End date must be after start date';
    if (['tentative', 'confirmed'].includes(statusName) && !start.isAfter(moment())) {
      return 'Start date must be in the future';
    }
    if (statusName === 'completed' && !end.isBefore(moment())) {
      return 'End date must be in the past';
    }
    if (statusName === 'active' && !start.isBefore(moment())) {
      return 'Start date must be in the past';
    }
    if (start.isBefore(moment(), 'day')) {
      const overlap = assignments.some(a => {
        if (a.id === assignmentId) return false;
        if (a.assetId !== assetId) return false;
        const aStart = moment(a.startDate || a.start_date);
        const aEnd = moment(a.endDate || a.end_date);
        return start.isSameOrBefore(aEnd, 'day') && end.isSameOrAfter(aStart, 'day');
      });
      if (overlap) {
        return 'Overlaps with existing assignment';
      }
    }
    return '';
  };

  const handleItemMove = async (itemId, dragTime) => {
    const item = items.find((it) => it.id === itemId);
    const data = assignments.find((a) => a.id === itemId);
    if (!item || !data) return;
    if (data.status2?.name?.toLowerCase().includes('complete')) return;
    const duration = item.end_time.diff(item.start_time, 'days');
    const newStart = moment(dragTime);
    const newEnd = moment(dragTime).add(duration, 'days');
    const err = validateDates(itemId, data.assetId, newStart, newEnd, data.status);
    if (err) {
      setToastMsg(err);
      return;
    }
    setItems((prev) =>
      prev.map((it) =>
        it.id === itemId ? { ...it, start_time: newStart, end_time: newEnd } : it
      )
    );
    await updateAssetAssignments(itemId, {
      startDate: newStart.format('YYYY-MM-DD'),
      endDate: newEnd.format('YYYY-MM-DD'),
    });
  };

  const handleItemResize = async (itemId, time, edge) => {
    const item = items.find((it) => it.id === itemId);
    const data = assignments.find((a) => a.id === itemId);
    if (!item || !data) return;
    if (data.status2?.name?.toLowerCase().includes('complete')) return;
    const newStart = edge === 'left' ? moment(time) : item.start_time;
    const newEnd = edge === 'left' ? item.end_time : moment(time);
    const err = validateDates(itemId, data.assetId, newStart, newEnd, data.status);
    if (err) {
      setToastMsg(err);
      return;
    }
    setItems((prev) =>
      prev.map((it) =>
        it.id === itemId ? { ...it, start_time: newStart, end_time: newEnd } : it
      )
    );
    await updateAssetAssignments(itemId, {
      startDate: newStart.format('YYYY-MM-DD'),
      endDate: newEnd.format('YYYY-MM-DD'),
    });
  };

  const changePeriod = (direction) => {
    const unit = viewMode === 'week' ? 'week' : viewMode === 'month' ? 'month' : viewMode === 'year' ? 'year' : 'day';
    setCurrentDate((prev) => moment(prev)[direction](1, unit));
  };

  return (
    <div className='card h-100 p-0'>
      {toastMsg && (
        <div className='toast-container position-fixed top-0 start-50 translate-middle-x p-3' style={{ zIndex: 2000 }}>
          <div className='toast show align-items-center text-bg-danger border-0'>
            <div className='d-flex'>
              <div className='toast-body'>{toastMsg}</div>
              <button type='button' className='btn-close btn-close-white me-2 m-auto' onClick={() => setToastMsg('')}></button>
            </div>
          </div>
        </div>
      )}
      <div className='card-header d-flex justify-content-between align-items-center'>
        <div className='d-flex flex-wrap align-items-center gap-2'>
          <div className='dropdown'>
            <button
              className='w-32-px h-32-px bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center me-6'
              type='button'
              onClick={() => setShowDropdown(prev => !prev)}
            >
              <Icon icon='mage:filter-2' className='icon text-xl line-height-1' />
            </button>
            <button
              id='reset-filters-btn'
              className='w-32-px h-32-px bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center me-6'
              onClick={() => resetFilters()}
            >
              <Icon icon='mage:multiply' className='icon text-xl line-height-1' />
            </button>
            <button
              id='apply-filters-btn'
              className='d-none w-32-px h-32-px bg-secondary-focus text-secondary-main rounded-circle d-inline-flex align-items-center justify-content-center me-6'
              onClick={() => applyFilters()}
            >
              <Icon icon='mage:check' className='icon text-xl line-height-1' />
            </button>
          </div>
          {renderFilterBadges()}
          {showDropdown && (
            <div style={{ marginTop: '40px' }}>
              {renderFilterDropdown()}
            </div>
          )}
          <div className='btn-group ms-3'>
            <button className={`btn btn-sm ${viewMode === 'week' ? 'btn-primary' : 'btn-light'}`} onClick={() => setViewMode('week')}>Week</button>
            <button className={`btn btn-sm ${viewMode === 'month' ? 'btn-primary' : 'btn-light'}`} onClick={() => setViewMode('month')}>Month</button>
            <button className={`btn btn-sm ${viewMode === 'year' ? 'btn-primary' : 'btn-light'}`} onClick={() => setViewMode('year')}>Year</button>
          </div>
          <div className='btn-group ms-2'>
            <button className='btn btn-sm btn-light' onClick={() => changePeriod('subtract')}>
              <Icon icon='mage:chevron-left' />
            </button>
            <button className='btn btn-sm btn-light' onClick={() => changePeriod('add')}>
              <Icon icon='mage:chevron-right' />
            </button>
          </div>
        </div>
        <button className='btn btn-primary btn-sm' onClick={handleAdd}>
          <Icon icon='ic:baseline-plus' className='icon text-xl line-height-1' />{' '}Add Assignment
        </button>
      </div>
      <div className='card-body p-0'>
        <Timeline
          groups={groups}
          items={items}
          stackItems
          visibleTimeStart={getRange()[0]}
          visibleTimeEnd={getRange()[1]}
          onTimeChange={(start, end, update) => {
            setCurrentDate(moment(start));
            update(start, end);
          }}
          canMove
          canResize="both"
          lineHeight={40}
          onItemClick={(id) => handleEdit(id)}
          onItemMove={handleItemMove}
          onItemResize={handleItemResize}
        />
        <div className='mt-3 px-3'>
          <small>Bar colours indicate project status</small>
        </div>
      </div>
      {showModal && (
        <div className='modal modal-backdrop show d-block' tabIndex='-1'>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>{editingId ? 'Edit Assignment' : 'Add Assignment'}</h5>
                <button type='button' className='btn-close' onClick={() => setShowModal(false)} />
              </div>
              <div className='modal-body'>
                <div className='row g-3'>
                  {assignmentFields.map(renderField)}
                </div>
              </div>
              <div className='modal-footer'>
                <button className='btn btn-primary' onClick={handleSave}>Save</button>
                <button className='btn btn-secondary' onClick={() => setShowModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetAssignmentLayer;